import {
  DataRoomFolder,
  DataRoomPermission,
  QuestionQuery,
} from "@/src/graphql/generated";
import { AnimatedModal } from "../../AnimatedModal";
import { H3 } from "../../Heading";
import { CloseIcon } from "../../CloseIcon";
import { Option } from "../../tailwind/Dropdown";
import { useEffect, useState } from "react";
import { MultiStepUploader } from "./MultiStepUploader";
import { useInvalidateQueryKeys } from "@/src/hooks/useInvalidateQueryKeys";

interface NewFilesUploaderProps {
  open: boolean;
  folder?: DataRoomFolder | undefined;
  question?: QuestionQuery["question"] | undefined;
  isLoading: boolean;
  error?: unknown;
  onClose: () => void;
  permissions: Pick<DataRoomPermission, "id" | "name" | "description">[];
  keepOpenDuringUpload?: boolean;
}

export interface fileWrapper {
  id: string;
  file: File;
  name: string;
  dataRoomPermissionId: string;
  selectedDataRoomFolderID?: string;
  uploadProgress: number;
  status:
    | "creating"
    | "uploading"
    | "complete"
    | "error"
    | "pending"
    | "cancelled";
  selectedPermission: Option;
  createdDataRoomFileId?: string;
  redacted?: boolean;
  parentFolder?: folderWrapper;
}

export interface folderWrapper {
  id: number;
  name: string;
  children?: folderWrapper[];

  files?: fileWrapper[];
  uploadProgress: number;
  status:
    | "creating"
    | "uploading"
    | "complete"
    | "error"
    | "pending"
    | "cancelled";
  selectedPermission?: Option;
}

export function NewFilesUploader(props: NewFilesUploaderProps) {
  const [folders, setFolders] = useState<{ id: string; name: string }[]>([]);

  const {
    invalidateDataRoomFolders,
    invalidateTransactionFiles,
    invalidateDealActivities,
  } = useInvalidateQueryKeys();

  useEffect(() => {
    if (props.isLoading || props.error || !props.folder) {
      return;
    }

    setFolders(props.folder.parentFolders);
  }, [props.folder, props.isLoading, props.error]);

  const crumbs = [
    "Home",
    ...folders
      .slice(0, folders.length - 1)
      .map((f) => f.name)
      .reverse(),
  ];

  function clearCaches() {
    invalidateDataRoomFolders();
    invalidateTransactionFiles();
    invalidateDealActivities();
  }

  if (props.isLoading) {
    return null;
  }

  return (
    <AnimatedModal
      padding="p-0"
      open={props.open}
      onClose={() => {
        clearCaches();
        props.onClose();
      }}
      size="xxxxl"
      closeOnOutsideClick={false}
    >
      <div>
        <div className="p-4 flex justify-between items-center z-50">
          <div>
            <H3>Upload documents</H3>
            <p className="text-sm text-gray-500 leading-none">
              In {`${crumbs.join(" / ")}`}
            </p>
          </div>
          <CloseIcon
            onClose={() => {
              props.onClose();
            }}
          />
        </div>
        {props.open &&
          (props.question ? (
            <MultiStepUploader
              permissions={props.permissions}
              questionId={props.question.id}
              closeOnSubmit={!props.keepOpenDuringUpload}
              onDone={() => {
                clearCaches();
                props.onClose();
              }}
            />
          ) : (
            <MultiStepUploader
              permissions={props.permissions}
              dataRoomFolderId={props.folder?.id}
              closeOnSubmit={!props.keepOpenDuringUpload}
              onDone={() => {
                clearCaches();
                props.onClose();
              }}
            />
          ))}
      </div>
    </AnimatedModal>
  );
}
